/**
 * Session and theme.
 *
 * The signed-in user, their permissions and the light/dark choice all live
 * here. `can()` mirrors the server's permission keys exactly — the interface
 * hides what a user cannot do, but the server is what actually refuses.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, refreshSession, setAccessToken, setUnauthorizedHandler } from "@/lib/api";
import { setClockFormat } from "./format";
import type { Organization, User } from "@/lib/types";

type Theme = "light" | "dark" | "system";

interface SessionValue {
  user: User | null;
  organization: Organization | null;
  permissions: Set<string>;
  loading: boolean;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  canAccessAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ administers: boolean }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<unknown>;
  adoptToken: (token: string) => Promise<{ administers: boolean }>;
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

const THEME_KEY = "alpharoom-theme";

/** Whether these permissions reach the administration area at all.
 *
 *  One rule, used by sign-in, by single sign-on and by the route that catches
 *  an already-signed-in person on /sign-in. Three copies of this drifted: the
 *  password path was fixed, the invitation path was fixed, and single sign-on
 *  and the route guard were not — so an administrator still landed in room
 *  search, which is what the fix was supposed to stop. */
export function administersWith(permissions: string[] | undefined): boolean {
  return (permissions ?? []).some((p) => ADMIN_PERMISSIONS.includes(p));
}

export const ADMIN_PERMISSIONS = [
  "room.manage", "location.manage", "policy.manage", "device.read", "user.manage",
  "role.manage", "org.manage", "integration.read", "auth.manage", "analytics.read",
  "audit.read", "health.read", "approval.decide", "booking.read.all", "plan.manage",
];

function readTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() =>
    resolve(readTheme()),
  );

  useEffect(() => {
    const applied = resolve(theme);
    setResolvedTheme(applied);
    document.documentElement.setAttribute("data-theme", applied);
    localStorage.setItem(THEME_KEY, theme);
    if (theme !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => {
      const next = media.matches ? "dark" : "light";
      setResolvedTheme(next);
      document.documentElement.setAttribute("data-theme", next);
    };
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [theme]);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setOrganization(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clear);
  }, [clear]);

  // Twelve- or twenty-four-hour is the customer's choice, made once in the
  // admin settings, and it has to reach every time this application writes.
  // Watching the organisation rather than each place that sets it means a
  // sign-in, a refresh and a reload all pick it up by the same route.
  useEffect(() => {
    setClockFormat(organization?.display_settings?.clock24h !== false);
  }, [organization]);

  const loadMe = useCallback(async () => {
    const body = await api.get<{ user: User; organization: Organization }>("/auth/me");
    setUser(body.user);
    setOrganization(body.organization);
    if (body.user.theme_preference) setThemeState(body.user.theme_preference);
    // Returned so a caller that navigates straight afterwards can decide where
    // to go without waiting for a render.
    return body;
  }, []);

  // On first load, try to restore a session from the refresh cookie.
  //
  // Through refreshSession, never POST /auth/refresh directly: it is the only
  // thing that collapses two of these into one request. This effect used to
  // ask by itself, and a refresh token is single-use — so a second copy of the
  // same cookie arriving a moment later looked to the server exactly like a
  // stolen one, and every session the person had was revoked. StrictMode runs
  // this effect twice on every load in development; two tabs restoring
  // together do the same thing to somebody real.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const body = await refreshSession<{
        access_token: string;
        user: User;
        organization: Organization;
      }>();
      if (cancelled) return;
      if (body === null) {
        clear();
      } else {
        setAccessToken(body.access_token);
        setUser(body.user);
        setOrganization(body.organization);
        if (body.user.theme_preference) setThemeState(body.user.theme_preference);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clear]);

  const signIn = useCallback(async (email: string, password: string) => {
    const body = await api.post<{ access_token: string; user: User; organization: Organization }>(
      "/auth/login",
      { email, password },
    );
    setAccessToken(body.access_token);
    setUser(body.user);
    setOrganization(body.organization);
    if (body.user.theme_preference) setThemeState(body.user.theme_preference);
    // Returned rather than read from context afterwards: the caller navigates
    // immediately, before a state update has rendered.
    return { administers: administersWith(body.user.permissions) };
  }, []);

  const adoptToken = useCallback(
    async (token: string) => {
      setAccessToken(token);
      const me = await loadMe();
      // Returned for the same reason signIn returns it: the caller navigates
      // immediately, before a state update has rendered. Reading it from
      // context there gets the previous value, which is how single sign-on
      // sent every administrator to the employee portal.
      return { administers: administersWith(me?.user.permissions) };
    },
    [loadMe],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      clear();
    }
  }, [clear]);

  const permissions = useMemo(
    () => new Set(user?.permissions ?? user?.role?.permissions ?? []),
    [user],
  );

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      if (user) void api.patch("/auth/me", { theme_preference: next }).catch(() => {});
    },
    [user],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      organization,
      permissions,
      loading,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      canAccessAdmin: ADMIN_PERMISSIONS.some((permission) => permissions.has(permission)),
      signIn,
      signOut,
      refresh: loadMe,
      adoptToken,
      theme,
      resolvedTheme,
      setTheme,
    }),
    [user, organization, permissions, loading, signIn, signOut, loadMe, adoptToken, theme, resolvedTheme, setTheme],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}
